import axiosConfig from '../config/axiosConfig';

// Cache for rates to avoid frequent API calls
let ratesCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fallback rates in case API fails
const FALLBACK_RATES = {
  MARKETING: { rate: '0.08', meta_rate: '0.88' },
  UTILITY: { rate: '0.08', meta_rate: '0.125' },
  AUTHENTICATION: { rate: '0.08', meta_rate: '0.125' }
};

/**
 * Fetch current rates from the API
 * @returns {Promise<Object>} Object with rates by category
 */
export const fetchCurrentRates = async () => {
  try {
    // Check cache first
    if (ratesCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      return ratesCache;
    }

    let ratesObject = {};

    // Try new public-rates endpoint first (single source of truth)
    try {
      const pubRes = await axiosConfig.get('/admin/platform/public-rates');
      const ratesMap = pubRes.data?.entity?.rates;
      if (ratesMap && Object.keys(ratesMap).length > 0) {
        Object.entries(ratesMap).forEach(([cat, data]) => {
          ratesObject[cat.toUpperCase()] = {
            rate: String(data.rate || 0),
            meta_rate: '0',  // public-rates only returns platform rate
            total_rate: String(data.rate || 0),
          };
        });
        // Update cache
        ratesCache = ratesObject;
        cacheTimestamp = Date.now();
        return ratesObject;
      }
    } catch (_) { /* fall through to legacy endpoint */ }

    // Fallback: legacy /rate/current endpoint
    const response = await axiosConfig.get('/rate/current');
    const rates = response.data.entity || [];
    
    // Convert array to object for easier lookup
    rates.forEach(rate => {
      ratesObject[rate.category.toUpperCase()] = {
        rate: rate.rate,
        meta_rate: rate.meta_rate,
        total_rate: (parseFloat(rate.rate) + parseFloat(rate.meta_rate)).toFixed(4)
      };
    });

    // Update cache
    ratesCache = ratesObject;
    cacheTimestamp = Date.now();
    
    return ratesObject;
  } catch (error) {
    console.error('Error fetching rates:', error);
    
    // Return fallback rates if API fails
    const fallbackRatesObject = {};
    Object.keys(FALLBACK_RATES).forEach(category => {
      const rate = FALLBACK_RATES[category];
      fallbackRatesObject[category] = {
        ...rate,
        total_rate: (parseFloat(rate.rate) + parseFloat(rate.meta_rate)).toFixed(4)
      };
    });
    
    return fallbackRatesObject;
  }
};

/**
 * Calculate message cost based on template category
 * @param {string} templateCategory - The template category (MARKETING, UTILITY, AUTHENTICATION)
 * @param {Object} rates - Optional rates object, if not provided will fetch from API
 * @returns {Promise<number>} Cost per message in INR
 */
export const calculateMessageCost = async (templateCategory, rates = null) => {
  try {
    const currentRates = rates || await fetchCurrentRates();
    const category = templateCategory?.toUpperCase() || 'MARKETING';
    
    // Get rate for the specific category, fallback to MARKETING if not found
    const categoryRate = currentRates[category] || currentRates['MARKETING'] || FALLBACK_RATES.MARKETING;
    
    if (!categoryRate) {
      // Ultimate fallback
      return parseFloat(FALLBACK_RATES.MARKETING.rate) + parseFloat(FALLBACK_RATES.MARKETING.meta_rate);
    }
    
    return parseFloat(categoryRate.total_rate || categoryRate.rate) + parseFloat(categoryRate.meta_rate || 0);
  } catch (error) {
    console.error('Error calculating message cost:', error);
    
    // Return fallback calculation
    const fallbackRate = FALLBACK_RATES[templateCategory?.toUpperCase()] || FALLBACK_RATES.MARKETING;
    return parseFloat(fallbackRate.rate) + parseFloat(fallbackRate.meta_rate);
  }
};

/**
 * Get rate breakdown for a specific category
 * @param {string} templateCategory - The template category
 * @param {Object} rates - Optional rates object
 * @returns {Promise<Object>} Object with platform_rate, meta_rate, and total_rate
 */
export const getRateBreakdown = async (templateCategory, rates = null) => {
  try {
    const currentRates = rates || await fetchCurrentRates();
    const category = templateCategory?.toUpperCase() || 'MARKETING';
    
    const categoryRate = currentRates[category] || currentRates['MARKETING'] || FALLBACK_RATES.MARKETING;
    
    return {
      platform_rate: parseFloat(categoryRate.rate || 0),
      meta_rate: parseFloat(categoryRate.meta_rate || 0),
      total_rate: parseFloat(categoryRate.total_rate || 0) || 
                  (parseFloat(categoryRate.rate || 0) + parseFloat(categoryRate.meta_rate || 0)),
      category: category
    };
  } catch (error) {
    console.error('Error getting rate breakdown:', error);
    
    const fallbackRate = FALLBACK_RATES[templateCategory?.toUpperCase()] || FALLBACK_RATES.MARKETING;
    return {
      platform_rate: parseFloat(fallbackRate.rate),
      meta_rate: parseFloat(fallbackRate.meta_rate),
      total_rate: parseFloat(fallbackRate.rate) + parseFloat(fallbackRate.meta_rate),
      category: templateCategory?.toUpperCase() || 'MARKETING'
    };
  }
};

/**
 * Clear the rates cache (useful when rates are updated)
 */
export const clearRatesCache = () => {
  ratesCache = null;
  cacheTimestamp = null;
};

/**
 * Preload rates into cache
 * @returns {Promise<Object>} Rates object
 */
export const preloadRates = async () => {
  return await fetchCurrentRates();
};